<template>
  <hit-modal
    popup-size="small"
    :cancellable="false"
    :top-aligned="true"
    @cancel="$emit('close')"
  >
    <div class="history-panel text-panel">
      <div class="history-card">
        <div class="card-header">
          <div class="card-icon card-icon-created">
            <hit-icon icon="add" />
          </div>
          <h3 class="card-title">
            {{ t('hit-base.common.created-by') }}
          </h3>
        </div>
        <div class="meta-list">
          <div class="meta-row">
            <span class="meta-label">
              <hit-icon icon="user-account" />
              {{ t('hit-app.common.user') }}
            </span>
            <span class="meta-value">{{ creator || '-' }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">
              <hit-icon icon="clock" />
              {{ t('hit-app.common.date') }}
            </span>
            <span class="meta-value">{{ formatDate(creationDate) }}</span>
          </div>
        </div>
      </div>

      <div class="history-card">
        <div class="card-header">
          <div class="card-icon card-icon-modified">
            <hit-icon icon="edit" />
          </div>
          <h3 class="card-title">
            {{ t('hit-base.common.modified-by') }}
          </h3>
        </div>
        <div class="meta-list">
          <div class="meta-row">
            <span class="meta-label">
              <hit-icon icon="user-account" />
              {{ t('hit-app.common.user') }}
            </span>
            <span class="meta-value">{{ modifier || '-' }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">
              <hit-icon icon="clock" />
              {{ t('hit-app.common.date') }}
            </span>
            <span class="meta-value">{{ formatDate(modificationDate) }}</span>
          </div>
        </div>
      </div>
    </div>
  </hit-modal>
</template>

<script>
import HitModal from '@hit/components/src/components/modal/HitModal.vue';
import {DataService} from '@hit/base/src';
import {useI18n} from 'vue-i18n';
import {HitIcon} from '@hit/components';
import Axios from 'axios';
import {globals} from '../../../globals';

export default {
  name: 'HitAppShowCreatedModified',
  components: {
    HitModal,
    HitIcon,
  },
  props: {
    table: {
      type: String,
      required: true,
    },
    itemId: {
      type: String,
      required: true,
    },
    useCustomApi: {
      type: Boolean,
      required: false,
      default: false,
    },
    isStaffTable: {
      type: Boolean,
      required: false,
      default: false,
    },
    customApiUrl: {
      type: String,
      required: false,
    },
    customApiRoute: {
      type: String,
      required: false,
    },
    customApiRoutePath: {
      type: String,
      required: false,
    },
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      creator: 'Hit-Online',
      modifier: 'Hit-Online',
      creationDate: null,
      modificationDate: null,
    };
  },
  beforeMount() {
    // For all the standard tables from the company scheme
    if (!this.useCustomApi) {
      if (!this.isStaffTable) {
        DataService.read(this.table, {
          attributes:
            'creation_date,modification_date,' +
            `creator:staff!fk_${this.table}_creation_user_id_staff(designation),` +
            `modifier:staff!fk_${this.table}_modification_user_id_staff(designation)`,
          filters: {id: 'eq.' + this.itemId},
        }).then((r) => {
          if (r && r.data && r.data.length > 0) {
            this.creator = r.data[0].creator?.designation ?? 'Hit-Online';
            this.modifier = r.data[0].modifier?.designation ?? null;
            this.creationDate = r.data[0].creation_date;
            this.modificationDate = r.data[0].modification_date;
          }
        });
      } else {
        // In the staff table, we can not add the FK, otherwise it always throws an error when adding the first user
        // We need to do it the old way
        DataService.read('staff', {
          attributes:
            'creation_date,modification_date,creation_user_id,modification_user_id',
          filters: {
            id: `eq.${this.itemId}`,
          },
        }).then((res) => {
          if (res && res.data && res.data.length > 0) {
            const filters = [];
            if (res.data[0].creation_user_id) {
              filters.push(`user_id.eq.${res.data[0].creation_user_id}`);
            }
            if (res.data[0].modification_user_id) {
              filters.push(`user_id.eq.${res.data[0].modification_user_id}`);
            }
            DataService.read('staff', {
              attributes: 'user_id,designation',
              filters: {
                or: `(${filters.join(',')})`,
              },
            }).then((r) => {
              if (r && r.data && r.data.length > 0) {
                this.creator =
                  r.data.find((x) => x.user_id === res.data[0].creation_user_id)
                    ?.designation ?? '';
                this.modifier =
                  r.data.find(
                    (x) => x.user_id === res.data[0].modification_user_id
                  )?.designation ?? '';
                this.creationDate = res.data[0].creation_date;
                this.modificationDate = res.data[0].modification_date;
              }
            });
          }
        });
      }
    } else {
      let config = {
        headers: {
          Authorization: 'Bearer ' + globals.$keycloak.token,
        },
        params: {
          filterId: this.itemId,
        },
      };

      const url =
        this.customApiUrl + this.customApiRoute + this.customApiRoutePath;

      Axios.get(url, config).then((response) => {
        const recordData = response.data[0];
        this.creationDate = recordData.creation_date;
        this.modificationDate = recordData.modification_date;
        if (recordData.creation_user_id) {
          config.params.filterId = recordData.creation_user_id;
          Axios.get(url, config).then((response) => {
            try {
              this.creator = `${response.data[0].first_name} ${response.data[0].last_name}`;
            } catch {
              this.creator = 'Hit-Online';
            }
          });
        } else {
          this.creator = 'Hit-Online';
        }

        if (recordData.modification_user_id) {
          config.params.filterId = recordData.modification_user_id;
          Axios.get(url, config).then((response) => {
            try {
              this.modifier = `${response.data[0].first_name} ${response.data[0].last_name}`;
            } catch {
              this.modifier = 'Hit-Online';
            }
          });
        } else {
          this.modifier = 'Hit-Online';
        }
      });
    }
  },
  methods: {
    formatDate(dateValue) {
      if (!dateValue) {
        return '-';
      }
      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) {
        return '-';
      }
      return parsedDate.toLocaleString();
    },
  },
};
</script>

<style scoped>
.history-panel {
  display: grid;
  gap: 0.875rem;
}

.panel-headline {
  display: flex;
  justify-content: flex-start;
}

.panel-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0.3rem 0.75rem;
  color: var(--color-accent, #00997c);
  border: 1px solid
    color-mix(in srgb, var(--color-accent, #00997c) 34%, transparent);
}

.history-card {
  border-radius: 0.9rem;
  border: 1px solid
    color-mix(
      in srgb,
      var(--color-accent, #00997c) 25%,
      var(--color-table, #e5e7eb)
    );
  padding: 0.95rem 1rem;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.8rem;
}

.card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 0.55rem;
  color: var(--color-accent, #00997c);
  border: 1px solid
    color-mix(in srgb, var(--color-accent, #00997c) 35%, transparent);
  background: color-mix(in srgb, var(--color-accent, #00997c) 16%, transparent);
}

.card-icon-modified {
  background: color-mix(in srgb, var(--color-accent, #00997c) 22%, transparent);
}

.card-title {
  font-size: 0.96rem;
  font-weight: 600;
}

.meta-list {
  display: grid;
  gap: 0.5rem;
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-radius: 0.6rem;
  padding: 0.5rem 0.6rem;
  background: color-mix(in srgb, var(--color-accent, #00997c) 8%, transparent);
}

.meta-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.8rem;
}

.meta-value {
  font-size: 0.83rem;
  font-weight: 600;
  text-align: right;
}
</style>
